import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Split,
  SplitItem,
  Stack,
  StackItem,
  TextInput,
} from '@patternfly/react-core';
import { MagicIcon, MinusCircleIcon, PlusCircleIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useMailDomainDnsZone } from '../../../hooks/useMailDomainDnsZone';
import { isApexName, rrsetErrorMessage } from '../Domains/zoneRecords';
import {
  SPF_BUILDER_TYPES,
  type SpfAllPolicy,
  type SpfBuilderType,
  type SpfSenderRow,
  parseSpf,
  serializeSpf,
  spfToBuilder,
  spfTypeRequiresValue,
  unwrapTxtValue,
} from './spfParse';
import type { ApiResponseDnsRrsetWrite } from '../../../types/api/ApiResponseDomainName';
import type { ApiResponseMailDomainDnsZone } from '../../../types/api/ApiResponseMailDomain';

interface SpfWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Bridge to the DNS side — the dns-zone state route is keyed by this. */
  organizationDomainId: number;
  domainName: string;
  /** The currently published SPF policy, used to prefill the builder (or `null` when none). */
  currentValue: string | null;
  /** Called after a successful publish so the SPF tab refetches. */
  onPublished: () => void;
}

const TYPE_LABELS: Record<SpfBuilderType, string> = {
  include: 'Include domain',
  ip4: 'IPv4 address',
  ip6: 'IPv6 address',
  a: 'A record',
  mx: 'MX hosts',
  exists: 'Exists',
};

const TYPE_HINTS: Record<SpfBuilderType, string> = {
  include: "Authorize another domain's SPF (a provider, e.g. _spf.google.com)",
  ip4: 'An IPv4 address or CIDR range',
  ip6: 'An IPv6 address or CIDR range',
  a: "Authorize this domain's (or another's) A/AAAA addresses",
  mx: "Authorize this domain's (or another's) MX hosts",
  exists: 'Authorize when a domain resolves (advanced)',
};

const TYPE_PLACEHOLDERS: Record<SpfBuilderType, string> = {
  include: '_spf.google.com',
  ip4: '198.51.100.0/24',
  ip6: '2001:db8::/32',
  a: 'example.com (optional)',
  mx: 'example.com (optional)',
  exists: '%{i}._spf.example.com',
};

const POLICY_OPTIONS: { value: SpfAllPolicy; label: string; description: string }[] = [
  {
    value: '-all',
    label: 'Reject (-all)',
    description: 'Strict — receivers reject mail from any other source. Recommended once every sender is listed.',
  },
  {
    value: '~all',
    label: 'Mark as suspicious (~all)',
    description: 'Soft fail — mail from other sources is accepted but flagged. Safe while rolling out SPF.',
  },
  {
    value: '?all',
    label: 'Neutral (?all)',
    description: 'No assertion is made about other sources — provides little protection.',
  },
  {
    value: '+all',
    label: 'Allow all (+all)',
    description: 'Authorizes every sender — this disables SPF. Not recommended.',
  },
];

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?$/;

/** Light, forgiving client-side check for a sender value (the backend validates authoritatively). */
function validateRow(type: SpfBuilderType, value: string): string | null {
  const v = value.trim();
  // Empty is fine for a/mx (a bare mechanism), but required types must be filled — flagging it both
  // shows the inline error and blocks Publish, rather than silently dropping the row from the record.
  if (!v) return spfTypeRequiresValue(type) ? 'Enter a value.' : null;
  if (/\s/.test(v)) return 'Cannot contain spaces.';
  switch (type) {
    case 'ip4':
      return IPV4.test(v) ? null : 'Enter an IPv4 address or CIDR (e.g. 198.51.100.0/24).';
    case 'ip6':
      return v.includes(':') ? null : 'Enter an IPv6 address or CIDR (e.g. 2001:db8::/32).';
    case 'include':
    case 'exists':
      return v.includes('.') || v.includes('%{') ? null : 'Enter a domain (e.g. _spf.google.com).';
    case 'a':
    case 'mx':
      return v.includes('.') || v.includes('%{') ? null : 'Enter a domain, or clear it to use this domain.';
    default:
      return null;
  }
}

/**
 * Guided SPF-record builder. The user assembles a list of allowed senders (type + value) and a final
 * policy; the resulting record string is previewed live at the top. When the domain's DNS zone is
 * managed here it can publish the record (apex TXT, preserving any non-SPF TXT siblings); otherwise it
 * explains that and offers the record for manual copy.
 */
function SpfWizardModal({
  isOpen,
  onClose,
  organizationDomainId,
  domainName,
  currentValue,
  onPublished,
}: SpfWizardModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { data: dz, loading: dnsLoading, error: dnsError, refetch: refetchDns } = useMailDomainDnsZone(
    organizationDomainId,
    isOpen,
  );

  const [rows, setRows] = useState<SpfSenderRow[]>([]);
  const [policy, setPolicy] = useState<SpfAllPolicy>('~all');
  const [unsupported, setUnsupported] = useState<string[]>([]);
  const [openSelectRowId, setOpenSelectRowId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idCounter = useRef(0);
  // Read `currentValue` non-reactively so the seed effect runs only on the open transition — a refetch
  // that replaces currentValue while the modal is open must not wipe the user's in-progress edits.
  const currentValueRef = useRef(currentValue);
  currentValueRef.current = currentValue;

  // Reset and seed the builder from the current record each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const builder = spfToBuilder(currentValueRef.current);
    setRows(builder.rows.map((r) => ({ ...r, id: `row-${idCounter.current++}` })));
    setPolicy(builder.policy);
    setUnsupported(builder.unsupported);
    setOpenSelectRowId(null);
    setCopied(false);
    setError(null);
    setIsSubmitting(false);
  }, [isOpen]);

  const preview = useMemo(() => serializeSpf(rows, policy), [rows, policy]);
  const previewWarnings = useMemo(() => parseSpf(preview).warnings, [preview]);

  const canPublish = dz?.zone_state === 'configured' && dz.domain_name_id !== null;
  const hasRowErrors = rows.some((r) => validateRow(r.type, r.value) !== null);

  const addRow = () =>
    setRows((prev) => [...prev, { id: `row-${idCounter.current++}`, type: 'include', value: '' }]);
  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));
  const updateRowType = (id: string, type: SpfBuilderType) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, type } : r)));
  const updateRowValue = (id: string, value: string) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)));
  const onCopy = (text: string) => {
    void navigator.clipboard?.writeText(text);
    setCopied(true);
  };

  const handlePublish = async () => {
    if (!dz || dz.domain_name_id === null) return;
    setIsSubmitting(true);
    setError(null);

    const apex = dz.domain;
    const domainNameId = dz.domain_name_id;
    const records = dz.zone?.records ?? [];
    // Preserve any non-SPF apex TXT records (domain verification, etc.) — replace only the SPF line. The
    // stored values are PowerDNS-quoted, so match on the unwrapped form (the backend re-normalizes what
    // we send, so the originals can be passed back as-is).
    const apexTxt = records.find((r) => r.type.toUpperCase() === 'TXT' && isApexName(r.name, apex));
    const siblings = (apexTxt?.content ?? []).filter(
      (v) => !unwrapTxtValue(v).toLowerCase().startsWith('v=spf1'),
    );
    const payload = { name: apex, type: 'TXT', ttl: apexTxt?.ttl ?? 3600, content: [...siblings, preview] };

    const send = (method: 'POST' | 'PATCH') =>
      apiFetch(`/datacenters/domain-names/${domainNameId}/dns/rrset`, {
        method,
        headers: { Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

    const notifyFailure = (reason: string) => {
      setError(reason);
      addNotification({
        title: 'Could not publish SPF record',
        srTitle: 'Error alert',
        description: reason,
        variant: 'danger',
      });
      setDrawerOpen(true);
    };

    try {
      let method: 'POST' | 'PATCH' = apexTxt ? 'PATCH' : 'POST';
      let response = await send(method);
      let body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;

      // Collision fallback: the open-time snapshot was stale (an apex TXT set now exists). Re-read the
      // zone so we merge with the REAL current TXT siblings instead of overwriting them, then replace in
      // place. (A plain replace would be safe for MX, but the apex TXT set can hold unrelated records.)
      const collided =
        method === 'POST' &&
        body?.status !== 'success' &&
        typeof body?.data?.name === 'string' &&
        body.data.name.toLowerCase().includes('already exists');
      if (collided) {
        try {
          const freshResp = await apiFetch(
            `/emails/domains/dns-zone?organization_domain_id=${organizationDomainId}`,
          );
          const freshBody = (await freshResp.json().catch(() => null)) as ApiResponseMailDomainDnsZone | null;
          const freshRecords =
            freshBody?.status === 'success' ? freshBody.data?.dns_zone.zone?.records ?? [] : [];
          const freshApexTxt = freshRecords.find(
            (r) => r.type.toUpperCase() === 'TXT' && isApexName(r.name, apex),
          );
          if (freshApexTxt) {
            payload.content = [
              ...freshApexTxt.content.filter((v) => !unwrapTxtValue(v).toLowerCase().startsWith('v=spf1')),
              preview,
            ];
            payload.ttl = freshApexTxt.ttl;
          }
        } catch {
          // Re-read failed — fall through to a plain replace with the payload we already have.
        }
        method = 'PATCH';
        response = await send('PATCH');
        body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;
      }

      if (body?.status === 'success' && body.data?.record_set) {
        addNotification({
          title: 'SPF record published',
          srTitle: 'Success alert',
          description: `The SPF record for ${apex} was published to DNS.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onPublished();
        onClose();
        return;
      }
      notifyFailure(rrsetErrorMessage(body) ?? `Request failed with status ${response.status}`);
    } catch (err) {
      notifyFailure(err instanceof Error ? err.message : 'An unexpected error occurred while publishing.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDnsSection = () => {
    if (dnsLoading) {
      return (
        <Bullseye className="pf-v6-u-mt-md">
          <Spinner size="md" aria-label="Checking the DNS zone" />
        </Bullseye>
      );
    }
    if (dnsError) {
      return (
        <Alert
          variant="warning"
          isInline
          title="Couldn't check the DNS zone"
          className="pf-v6-u-mt-md"
          actionLinks={<AlertActionLink onClick={() => void refetchDns()}>Retry</AlertActionLink>}
        >
          {dnsError} You can still copy the record above and add it at your DNS provider as an apex TXT record.
        </Alert>
      );
    }
    if (!canPublish) {
      return (
        <Alert
          variant="info"
          isInline
          title="This zone isn't managed by the Manager"
          className="pf-v6-u-mt-md"
        >
          {dz?.zone_error ? `${dz.zone_error} ` : ''}
          The record can&apos;t be saved to DNS directly. Copy it above and add it at your DNS provider as a
          TXT record on the apex (<code>{domainName}</code>).
        </Alert>
      );
    }
    const zoneRecords = dz?.zone?.records ?? [];
    const apexTxtHasSpf = zoneRecords.some(
      (r) =>
        r.type.toUpperCase() === 'TXT' &&
        isApexName(r.name, dz!.domain) &&
        r.content.some((c) => unwrapTxtValue(c).toLowerCase().startsWith('v=spf1')),
    );
    // A deprecated RFC 4408 SPF-type record is a separate RRSet the TXT write doesn't touch; leaving it
    // would mean two SPF records (permerror), so warn the user to remove it manually.
    const legacySpfTypeExists = zoneRecords.some(
      (r) => r.type.toUpperCase() === 'SPF' && isApexName(r.name, dz!.domain),
    );
    return (
      <>
        <Alert variant="info" isInline isPlain title="Ready to publish" className="pf-v6-u-mt-md">
          Publishing writes this record as a TXT record on <code>{domainName}</code>’s apex.
          {apexTxtHasSpf ? ' The existing SPF record is replaced; other TXT records are kept.' : ''}
        </Alert>
        {legacySpfTypeExists && (
          <Alert
            variant="warning"
            isInline
            title="A legacy SPF-type record also exists"
            className="pf-v6-u-mt-sm"
          >
            This domain also has a deprecated SPF-type DNS record on the apex. Publishing won&apos;t remove
            it, and two SPF records cause a permanent error — delete the SPF-type record at your DNS provider.
          </Alert>
        )}
      </>
    );
  };

  const renderFooter = () => {
    if (dnsLoading) {
      return (
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
      );
    }
    if (canPublish) {
      return (
        <>
          <Button
            variant="primary"
            icon={<MagicIcon />}
            onClick={() => void handlePublish()}
            isLoading={isSubmitting}
            isDisabled={isSubmitting || hasRowErrors}
          >
            Publish to DNS
          </Button>
          <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
        </>
      );
    }
    return (
      <Button variant="primary" onClick={onClose}>
        Close
      </Button>
    );
  };

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      onEscapePress={() => (openSelectRowId !== null ? setOpenSelectRowId(null) : onClose())}
      aria-label={`Build SPF record for ${domainName}`}
    >
      <ModalHeader title="Build SPF record" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Could not publish" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}

        <Content component="h2" className="pf-v6-u-font-size-sm pf-v6-u-mb-xs">
          Resulting record
        </Content>
        <CodeBlock
          actions={
            <CodeBlockAction>
              <ClipboardCopyButton
                id="spf-wizard-copy"
                aria-label="Copy SPF record to clipboard"
                variant="plain"
                onClick={() => onCopy(preview)}
                exitDelay={copied ? 1500 : 600}
                maxWidth="110px"
                onTooltipHidden={() => setCopied(false)}
              >
                {copied ? 'Copied!' : 'Copy to clipboard'}
              </ClipboardCopyButton>
            </CodeBlockAction>
          }
        >
          <CodeBlockCode>{preview}</CodeBlockCode>
        </CodeBlock>
        {previewWarnings.length > 0 && (
          <FormHelperText>
            <HelperText>
              {previewWarnings.map((w, i) => (
                <HelperTextItem key={i} variant="warning">
                  {w}
                </HelperTextItem>
              ))}
            </HelperText>
          </FormHelperText>
        )}

        {unsupported.length > 0 && (
          <Alert
            variant="warning"
            isInline
            title="Some of the current record can't be edited here"
            className="pf-v6-u-mt-md"
          >
            The builder can&apos;t represent{' '}
            <code style={{ wordBreak: 'break-all' }}>{unsupported.join(' ')}</code>. If you publish, the record
            is replaced and those parts are dropped.
          </Alert>
        )}

        <Form className="pf-v6-u-mt-md">
          <FormGroup label="Allowed senders" fieldId="spf-senders">
            <Stack hasGutter>
              {rows.map((row, i) => {
                const rowError = validateRow(row.type, row.value);
                return (
                  <StackItem key={row.id}>
                    <Split hasGutter>
                      <SplitItem>
                        <Select
                          isOpen={openSelectRowId === row.id}
                          selected={row.type}
                          onSelect={(_event, value) => {
                            updateRowType(row.id, value as SpfBuilderType);
                            setOpenSelectRowId(null);
                          }}
                          onOpenChange={(open) => setOpenSelectRowId(open ? row.id : null)}
                          toggle={(toggleRef) => (
                            <MenuToggle
                              ref={toggleRef}
                              onClick={() =>
                                setOpenSelectRowId(openSelectRowId === row.id ? null : row.id)
                              }
                              isExpanded={openSelectRowId === row.id}
                              isDisabled={isSubmitting}
                              aria-label="Sender type"
                              style={{ width: '190px' }}
                            >
                              {TYPE_LABELS[row.type]}
                            </MenuToggle>
                          )}
                        >
                          <SelectList>
                            {SPF_BUILDER_TYPES.map((t) => (
                              <SelectOption key={t} value={t} description={TYPE_HINTS[t]}>
                                {TYPE_LABELS[t]}
                              </SelectOption>
                            ))}
                          </SelectList>
                        </Select>
                      </SplitItem>
                      <SplitItem isFilled>
                        <TextInput
                          id={i === 0 ? 'spf-senders' : undefined}
                          type="text"
                          value={row.value}
                          onChange={(_event, value) => updateRowValue(row.id, value)}
                          aria-label={`${TYPE_LABELS[row.type]} value`}
                          placeholder={TYPE_PLACEHOLDERS[row.type]}
                          validated={rowError ? 'error' : 'default'}
                          isDisabled={isSubmitting}
                        />
                      </SplitItem>
                      <SplitItem>
                        <Button
                          variant="plain"
                          aria-label={`Remove sender ${i + 1}`}
                          icon={<MinusCircleIcon />}
                          onClick={() => removeRow(row.id)}
                          isDisabled={isSubmitting}
                        />
                      </SplitItem>
                    </Split>
                    {rowError && (
                      <FormHelperText>
                        <HelperText>
                          <HelperTextItem variant="error">{rowError}</HelperTextItem>
                        </HelperText>
                      </FormHelperText>
                    )}
                  </StackItem>
                );
              })}
              <StackItem>
                <Button variant="link" icon={<PlusCircleIcon />} onClick={addRow} isDisabled={isSubmitting}>
                  Add sender
                </Button>
              </StackItem>
            </Stack>
          </FormGroup>

          <FormGroup label="All other senders" fieldId="spf-policy">
            <FormSelect
              id="spf-policy"
              value={policy}
              onChange={(_event, value) => setPolicy(value as SpfAllPolicy)}
              isDisabled={isSubmitting}
              aria-label="Policy for all other senders"
            >
              {POLICY_OPTIONS.map((o) => (
                <FormSelectOption key={o.value} value={o.value} label={o.label} />
              ))}
            </FormSelect>
            <FormHelperText>
              <HelperText>
                <HelperTextItem>{POLICY_OPTIONS.find((o) => o.value === policy)?.description}</HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>

        {renderDnsSection()}
      </ModalBody>
      <ModalFooter>{renderFooter()}</ModalFooter>
    </Modal>
  );
}

export default SpfWizardModal;
